import { type ValidationMode } from '@wix/ambassador-forms-v1-validation-result/types';
import {
  type ValidationArrayTypeArrayItems,
  type FieldsInputField,
  type ValidationArrayType,
  ArrayItemsItemType,
} from '@wix/ambassador-forms-v4-form/types';
import {
  type PartialSchema,
  type SomeJSONSchema,
} from 'ajv/dist/types/json-schema';

import { asRequired, isDefined } from '../../../utils/lang';
import { setValidationProperties } from '../../../utils/validation';
import { mapToBooleanSchemaProperty } from '../boolean-schema-property-mapper';
import { mapToNumberSchemaProperty } from '../number-schema-property-mapper';
import { mapToObjectSchemaProperty } from '../object-schema-property-mapper';
import { PropertyTypes } from '../property-types';
import { mapToStringSchemaProperty } from '../string-schema-property-mapper';

const ARRAY_VALIDATION_PROPERTIES = ['minItems', 'maxItems'];
const ALLOWED_MAX_ITEMS = 100;

export function arraySchemaPropertyMapperPlatformised(
  inputField: FieldsInputField,
  validationMode: ValidationMode,
  libraryBasedPhoneFieldValidationEnabled: boolean = false,
): PartialSchema<[]> {
  const arrayInput = asRequired(
    inputField.arrayOptions,
    inputField,
    'arrayOptions',
  );
  const optionsValues = getValuesFromOptions(
    isDefined(arrayInput.checkboxGroupOptions?.customOption),
    arrayInput.checkboxGroupOptions?.options,
  );
  const arrayValidation: ValidationArrayType = asRequired(
    arrayInput.validation,
    arrayInput,
    'validation',
  );
  const itemsValidation = resolveArrayItemsValidation(
    validationMode,
    arrayValidation.items,
    optionsValues,
    libraryBasedPhoneFieldValidationEnabled,
  );

  return {
    type: PropertyTypes.ARRAY,
    ...setValidationProperties<number>(
      ARRAY_VALIDATION_PROPERTIES,
      arrayValidation,
      {
        maxItems: (value) =>
          Math.min(value ?? ALLOWED_MAX_ITEMS, ALLOWED_MAX_ITEMS),
      },
    ),
    ...(itemsValidation && { items: itemsValidation }),
  };
}

function resolveArrayItemsValidation(
  validationMode: ValidationMode,
  arrayItems?: ValidationArrayTypeArrayItems,
  optionsValues: unknown[] = [],
  libraryBasedPhoneFieldValidationEnabled: boolean = false,
): Omit<SomeJSONSchema, '$id'> | undefined {
  switch (arrayItems?.itemType ?? ArrayItemsItemType.UNKNOWN_ITEM_TYPE) {
    case ArrayItemsItemType.STRING:
      return mapToStringSchemaProperty(
        makeEnumValidation(arrayItems?.stringOptions!, optionsValues),
        validationMode,
        libraryBasedPhoneFieldValidationEnabled,
      );
    case ArrayItemsItemType.NUMBER:
      return mapToNumberSchemaProperty(
        makeEnumValidation(arrayItems?.numberOptions!, optionsValues),
        validationMode,
      );
    case ArrayItemsItemType.BOOLEAN:
      return mapToBooleanSchemaProperty(
        makeEnumValidation(arrayItems?.booleanOptions!, optionsValues),
        validationMode,
      );
    case ArrayItemsItemType.OBJECT:
      return mapToObjectSchemaProperty(
        arrayItems?.objectOptions!,
        validationMode,
      );
    default:
      throw Error('Required to specify items type for array');
  }
}

export function getValuesFromOptions<T>(
  customOptionDefined: boolean,
  options?: {
    value?: T | null;
  }[],
): T[] | undefined {
  if (customOptionDefined || !isDefined(options)) {
    return undefined;
  }
  return options.map((_) => _.value).filter(isDefined);
}

export function makeEnumValidation<E, T extends { enum?: E[] | null }>(
  enumeratableValidation: T,
  optionsValues?: E[],
) {
  if (enumeratableValidation.enum?.length) {
    return enumeratableValidation;
  } else {
    return {
      ...enumeratableValidation,
      enum: optionsValues,
    };
  }
}
